import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import EnterpriseBulkPaymentFlowV2 from '../components/Payment/EnterpriseBulkPaymentFlowV2';
import BulkWithdrawalFlow from '../components/Payment/BulkWithdrawalFlow';
import BulkFpmGenerationFlow from '../components/Payment/BulkFpmGenerationFlow';
import StreamProgressPanel from '../components/Payment/StreamProgressPanel';
import { api } from '../services/api';
import { streamNdjson } from '../services/streamNdjson';

/**
 * PAGE : Reprise de données
 *
 * Regroupe les outils d'import/recalcul utilisés lors des reprises
 * de données ou opérations de maintenance :
 *  - Reprise épargne FSR (import cotisations FSR au format simplifié)
 *  - Reprise des rachats (import rachats FSR)
 *  - Recalcul global des polices d'épargne FSR (one-shot)
 */
const AdminDataMigration = () => {
  const [showBulkV2Modal, setShowBulkV2Modal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showFpmModal, setShowFpmModal] = useState(false);
  const [showRecomputeConfirm, setShowRecomputeConfirm] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeProgress, setRecomputeProgress] = useState(null);
  const [lastRecomputeResult, setLastRecomputeResult] = useState(null);

  const handleCloseBulkV2      = () => setShowBulkV2Modal(false);
  const handleCloseWithdrawal  = () => setShowWithdrawalModal(false);
  const handleCloseFpm         = () => setShowFpmModal(false);

  const handleRecomputeAllFsr = async () => {
    setRecomputing(true);
    setRecomputeProgress({
      current: 0, total: 0, currentLabel: '',
      counters: { processed: 0, skipped: 0, errors: 0 }
    });
    try {
      const baseURL = api.defaults.baseURL || '';
      const finalResult = await streamNdjson({
        url: `${baseURL}/payments/recompute-fsr-savings?stream=1`,
        body: {},
        onEvent: (evt) => {
          if (evt.type === 'start') {
            setRecomputeProgress((p) => ({ ...(p || {}), total: evt.total || 0 }));
          } else if (evt.type === 'policy') {
            setRecomputeProgress((p) => ({
              ...(p || {}),
              current:      evt.current ?? p?.current ?? 0,
              total:        evt.total   ?? p?.total   ?? 0,
              currentLabel: evt.currentLabel || p?.currentLabel || '',
              counters: {
                processed: evt.processed    ?? p?.counters?.processed ?? 0,
                skipped:   evt.skipped      ?? p?.counters?.skipped   ?? 0,
                errors:    evt.errorsCount  ?? p?.counters?.errors    ?? 0
              }
            }));
          }
        }
      });
      setLastRecomputeResult(finalResult || null);
      toast.success(
        finalResult
          ? `Recalcul terminé : ${finalResult.processed}/${finalResult.totalPolicies} police(s) traitée(s)`
          : 'Recalcul terminé'
      );
      setShowRecomputeConfirm(false);
    } catch (err) {
      toast.error(err?.message || 'Erreur lors du recalcul');
    } finally {
      setRecomputing(false);
    }
  };

  const recomputeProgressForPanel = recomputeProgress ? {
    current:      recomputeProgress.current,
    total:        recomputeProgress.total,
    currentLabel: recomputeProgress.currentLabel,
    metrics: [
      { label: 'Traitées',  value: recomputeProgress.counters?.processed || 0, tone: 'emerald' },
      { label: 'Ignorées',  value: recomputeProgress.counters?.skipped   || 0, tone: 'amber'   },
      { label: 'Erreurs',   value: recomputeProgress.counters?.errors    || 0, tone: (recomputeProgress.counters?.errors || 0) > 0 ? 'red' : 'gray' }
    ]
  } : null;

  const cards = [
    {
      id: 'bulk-v2',
      title: 'Reprise épargne FSR',
      description:
        "Import de cotisations épargne FSR via fichier au format simplifié. "
        + 'Crée les souscriptions, polices, comptes d\'épargne et paiements '
        + 'manquants, et relance le recalcul des polices concernées.',
      icon: DocumentTextIcon,
      color: 'bg-indigo-600',
      actionLabel: 'Ouvrir',
      onClick: () => setShowBulkV2Modal(true)
    },
    {
      id: 'bulk-fpm-generation',
      title: 'Reprise de cotisation FPM',
      description:
        "Génération rétroactive des cotisations, paiements et reçus pour "
        + 'toutes les polices FPM ACTIVE, depuis leur date d\'effet jusqu\'à '
        + 'la période (année + mois) choisie. Aucun fichier requis.',
      icon: DocumentTextIcon,
      color: 'bg-emerald-600',
      actionLabel: 'Ouvrir',
      onClick: () => setShowFpmModal(true)
    },
    {
      id: 'bulk-withdrawals',
      title: 'Reprise des rachats',
      description:
        "Import des rachats d'épargne FSR via fichier (RACHAT_FSR.xlsx). "
        + 'Crée les opérations de rachat (totaux et partiels), les paiements '
        + 'associés et met à jour les soldes des comptes d\'épargne.',
      icon: DocumentTextIcon,
      color: 'bg-rose-600',
      actionLabel: 'Ouvrir',
      onClick: () => setShowWithdrawalModal(true)
    },
    {
      id: 'recompute-fsr',
      title: "Recalcul global épargne FSR",
      description:
        "Recalcul one-shot de toutes les polices d'épargne FSR : "
        + 'suppression des intérêts existants, recalcul du cumul et du solde, '
        + 'regénération des intérêts annuels à partir de 2020.',
      icon: ArrowPathIcon,
      color: 'bg-amber-600',
      actionLabel: 'Lancer',
      onClick: () => setShowRecomputeConfirm(true)
    }
  ];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">Reprise de données</h1>
        <p className="text-gray-600">
          Outils d'import et de recalcul utilisés lors des reprises de données ou opérations
          de maintenance. À utiliser avec précaution — ces opérations impactent les données
          métier.
        </p>
      </div>

      {/* Cartes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.id}
              className="bg-white rounded-xl shadow-card p-6 border border-gray-200 hover:shadow-lg transition-shadow duration-200"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className={`${c.color} p-4 rounded-full`}>
                  <Icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{c.title}</h3>
                <p className="text-sm text-gray-600">{c.description}</p>
                <button
                  onClick={c.onClick}
                  className="w-full mt-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors duration-200 text-sm font-medium"
                >
                  {c.actionLabel}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dernier résultat de recalcul */}
      {lastRecomputeResult && (
        <div className="bg-white rounded-xl shadow-card p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Dernier recalcul FSR</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Polices totales</div>
              <div className="text-xl font-bold text-gray-900">
                {lastRecomputeResult.totalPolicies ?? '-'}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Traitées</div>
              <div className="text-xl font-bold text-green-600">
                {lastRecomputeResult.processed ?? '-'}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Ignorées</div>
              <div className="text-xl font-bold text-yellow-600">
                {lastRecomputeResult.skipped ?? '-'}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Erreurs</div>
              <div className="text-xl font-bold text-red-600">
                {lastRecomputeResult.errors?.length ?? 0}
              </div>
            </div>
          </div>
          {Array.isArray(lastRecomputeResult.errors) && lastRecomputeResult.errors.length > 0 && (
            <div className="mt-4 max-h-48 overflow-y-auto text-xs bg-red-50 border border-red-200 rounded p-3 text-red-700">
              <ul className="list-disc list-inside space-y-1">
                {lastRecomputeResult.errors.slice(0, 50).map((e, i) => (
                  <li key={i}>
                    {typeof e === 'string' ? e : (e.policyNumber || e.policyId || '?') + ' — ' + (e.message || JSON.stringify(e))}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Modal Reprise épargne FSR */}
      {showBulkV2Modal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseBulkV2}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white p-2 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      Reprise épargne FSR
                    </h3>
                    <p className="text-sm text-indigo-100">Import des cotisations FSR — format simplifié</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseBulkV2}
                  className="text-indigo-100 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="bg-white px-6 py-6 max-h-[calc(90vh-120px)] overflow-y-auto">
              <EnterpriseBulkPaymentFlowV2
                onCompleted={() => {
                  toast.success('Reprise épargne FSR effectuée avec succès');
                  handleCloseBulkV2();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Reprise de cotisation FPM */}
      {showFpmModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseFpm}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white p-2 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      Reprise de cotisation FPM
                    </h3>
                    <p className="text-sm text-emerald-100">Génération rétroactive des cotisations et paiements FPM</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseFpm}
                  className="text-emerald-100 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="bg-white px-6 py-6 max-h-[calc(90vh-120px)] overflow-y-auto">
              <BulkFpmGenerationFlow
                onCompleted={() => {
                  toast.success('Reprise de cotisation FPM effectuée avec succès');
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Reprise des rachats */}
      {showWithdrawalModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseWithdrawal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-rose-600 to-rose-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white p-2 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-rose-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      Reprise des rachats
                    </h3>
                    <p className="text-sm text-rose-100">Import des rachats FSR — RACHAT_FSR.xlsx</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseWithdrawal}
                  className="text-rose-100 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="bg-white px-6 py-6 max-h-[calc(90vh-120px)] overflow-y-auto">
              <BulkWithdrawalFlow
                onCompleted={() => {
                  toast.success('Reprise des rachats effectuée avec succès');
                  handleCloseWithdrawal();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation du recalcul global */}
      {showRecomputeConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => !recomputing && setShowRecomputeConfirm(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-amber-50 px-6 py-4 border-b border-amber-200 flex items-center gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
              <h3 className="text-lg font-semibold text-amber-900">Confirmer le recalcul</h3>
            </div>
            <div className="px-6 py-4 text-sm text-gray-700 space-y-4">
              <p>
                Cette opération va <strong>supprimer tous les intérêts existants</strong> sur
                les polices d'épargne FSR, puis recalculer le cumul, le solde et regénérer
                les intérêts annuels depuis 2020.
              </p>
              <p>L'opération peut prendre plusieurs minutes. Continuer ?</p>

              {recomputing && recomputeProgressForPanel && (
                <StreamProgressPanel
                  title="Recalcul en cours"
                  progress={recomputeProgressForPanel}
                  barColor="amber"
                />
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowRecomputeConfirm(false)}
                disabled={recomputing}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleRecomputeAllFsr}
                disabled={recomputing}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {recomputing && (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                )}
                {recomputing ? 'Recalcul en cours...' : 'Lancer le recalcul'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDataMigration;
